import { useState, useMemo, useEffect } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { Box, Card, CardContent } from '@mui/material';
import { toast } from 'react-toastify';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { Loading } from '@bumble/app/components/Loading';
import SplitPanes from '@bumble/app/components/SplitPanes';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import { Indicator } from '@bumble/app/components/Indicator';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import EditIcon from '@mui/icons-material/Edit';
import SpeedIcon from '@mui/icons-material/Speed';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import BumbleTabs from '@bumble/app/components/BumbleTabs';
import { PageLoading } from '@bumble/app/components/PageLoading';
import {
  EthernetDeviceCreate,
  EthernetDeviceTabDetails,
  EthernetDeviceNIDInfoEdit,
  EthernetDeviceNetflowEdit,
  EthernetDeviceOamCcmInfoEdit,
  EthernetDeviceActivationTest,
  EthernetDeviceDownloadResult,
  EthernetAssignEntity,
  EthernetDeviceGeoVisibilityEdit,
  EthernetDeviceResetLANPort,
  EthernetDeviceDDosMitigationEdit,
  EthernetDeviceBlockMaliciousEdit
} from '@bumble/app/components/ethernet-device';
import { useMutation, useQuery } from '@tanstack/react-query';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { MetroInterface, MetroInterfaces } from '@bumble/app/types/metroTypes';
import { useAccount } from '../hooks/use-account';
import { apiError } from '../utils/api-error';
import Select from 'react-select';
import { RouterGetDiagnostic, RouterPingAndTraceroute } from '../components/router';
import NodeMetrics from '../components/NodeMetrics';

export const EthernetDevices = () => {
  const api = useApi();
  const {account} = useAccount();

  const columns: GridColDef[] = [
    {
      field: 'metro_name',
      headerName: 'Name',
      minWidth: 300,
      disableColumnMenu: true,
    },
    {
      field: 'metro_id',
      headerName: 'Device ID',
      minWidth: 200,
      disableColumnMenu: true,
    },
    {
      field: 'prod_code',
      headerName: 'Product Code',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'admin_state',
      headerName: 'Admin State',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'op_state',
      headerName: 'Op State',
      width: 150,
      disableColumnMenu: true,
      renderCell: (params) => {
        return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
      }
    },
    {
      field: 'provider_service_id',
      headerName: 'Provider Service ID',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'client_circuit_id',
      headerName: 'Client Circuit ID',
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: 'entity_id',
      headerName: 'Entity ID',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'account_name',
      headerName: 'Account Name',
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: 'entity_name',
      headerName: 'Entity Name',
      width: 200,
      disableColumnMenu: true,
    },
    { field: 'site_address' },
    { field: 'shipping_address' },
  ];

  const columnVisible = {
    metro_name: true,
    metro_id: true,
    prod_code: true,
    provider_service_id: true,
    client_circuit_id: true,
    site_address: false,
    shipping_address: false,
    admin_state: true,
    op_state: true,
    entity_id: true,
    account_name: account?.master_account,
    entity_name: account?.master_account,
  };

  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [selectedItemWanInfo, setSelectedItemWanInfo] = useState<MetroInterface>();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showCreateDeviceDialog, setShowCreateDeviceDialog] = useState<boolean>(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showEditNIDInfoDialog, setShowEditNIDInfoDialog] = useState<boolean>(false);
  const [showEditOamCcmInfoDialog, setShowEditOamCcmInfoDialog] = useState<boolean>(false);
  const [showEditNetflowDialog, setShowEditNetflowDialog] = useState<boolean>(false);
  const [showEditGeoDialog, setShowEditGeoDialog] = useState<boolean>(false);
  const [showEditBlockMalicious, setShowEditBlockMalicious] = useState<boolean>(false);
  const [showEditDDosMitigation, setShowEditDDosMitigation] = useState<boolean>(false);
  const [showGetDiagnosticDialog, setShowGetDiagnosticDialog] = useState<boolean>(false);
  const [showResetLANPortsDialog, setShowResetLANPortsDialog] = useState<boolean>(false);
  const [showResetDeviceDialog, setShowResetDeviceDialog] = useState<boolean>(false);
  const [showPingDialog, setShowPingDialog] = useState<boolean>(false);
  // const [showLoopbackTestDialog, setShowLoopbackTestDialog] = useState<boolean>(false);
  const [showActivationTestDialog, setShowActivationTestDialog] = useState<boolean>(false);
  const [showRetrieveResultDialog, setShowRetrieveResultDialog] = useState<boolean>(false);
  const [showAssignEntity, setShowAssignEntity] = useState<boolean>(false);

  const [entities, setEntities] = useState<any[]>([{label: 'None', value: 'none'}]);
  const [entity, setEntity] = useState<any>(entities[0]);

  const { isLoading: isLoading2, data: entityNames } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    const entity_data = entityNames?.data;
    let actualEntities = [{label: 'None', value: 'none'}];
    if (entity_data && entity_data.length) {
      actualEntities = actualEntities.concat(entity_data.map((e: string) => ({
        label: e,
        value: e,
      })))
    }
    setEntities(actualEntities);
    setEntity(actualEntities[0]);
  }, [entityNames]);

  const changeEntity = (val: any) => {
    setEntity(val);
  };

  const { isLoading, data, refetch } = useQuery(
    ['metros', entity],
    api.getMetroApiV1GetMetroGet.bind(
      null,
      entity?.value && entity.value !== 'none' ? { entity_name: entity.value } : {}
    ),
    { enabled: true },
  );

  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);

  const removeMetro = useMutation(api.removeMetroApiV1RemoveMetroPost, {
    onSuccess() {
      toast.success('Metro device removed.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  });

  const {
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['metroDetail'],
    api.getMetroDetailApiV1GetMetroDetailGet.bind(null, {
      metro_id: selectedRowItem,
    }),
    { enabled: false },
  );

  useEffect(() => {
    if (selectedRowItem) detailRefetch();
  }, [detailRefetch, selectedRowItem]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else setSelectedItemId('');
  }, [rowSelectionModel, selectedRowItem]);

  useEffect(() => {
    const ports = detailData?.data.ports as MetroInterfaces;
    for (let itf in ports) {
      if (ports[itf] && ports[itf]['role'].toLowerCase().startsWith('wan')) {
        setSelectedItemWanInfo(ports[itf]);
      }
    }
  }, [detailData]);

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection.slice(-1));
  };

  const handleCreateModalOpen = () => {
    setShowCreateDeviceDialog(true);
  };

  const handleCreateModalClose = () => {
    setShowCreateDeviceDialog(false);
    handleDataRefetch();
  };

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Metro Ethernet NIDs.');
  };

  const handleMetroDelete = () => {
    setLoadingModalOpen(true);
    removeMetro.mutate({
      metro_id: selectedItemId,
    });
  };

  const resetNode = useMutation(api.resetNodeApiV1ResetNodePost, {
    onSuccess() {
      toast.success('Metro ethernet NID reseted successully.');
      setLoadingModalOpen(false);
      handleDataRefetch();
    },
    onError(error: any) {
      apiError(error);
      setLoadingModalOpen(false);
    },
  })

  const handleRouterResetDevice = () => {
    setLoadingModalOpen(true);
    resetNode.mutate({
      node_id: selectedItemId,
      hard: true,
    })
  };

  const handleEditNIDInfo = () => {
    setShowEditNIDInfoDialog(true);
  };

  const handleEditOamCcmInfo = () => {
    setShowEditOamCcmInfoDialog(true);
  };

  const handleEditNetflow = () => {
    setShowEditNetflowDialog(true);
  };

  const handleEditGeo = () => {
    setShowEditGeoDialog(true);
  };

  const handleBlockMalicious = () => {
    setShowEditBlockMalicious(true);
  };

  const handleDDosMitigation = () => {
    setShowEditDDosMitigation(true);
  };

  const handleGetDiagnostic = () => {
    setShowGetDiagnosticDialog(true);
  };

  const handleResetLAN = () => {
    setShowResetLANPortsDialog(true);
  };

  const handleResetDevice = () => {
    setShowResetDeviceDialog(true);
  };

  const handlePing = () => {
    setShowPingDialog(true);
  };

  // const handleLoopbackTest = () => {
  //   setShowLoopbackTestDialog(true);
  // };

  const handleActivationTest = () => {
    setShowActivationTestDialog(true);
  };

  const handleActivationTestDialogClose = (metroId: string, sse_id: string) => {
    setShowActivationTestDialog(false);
    if (sse_id) {
      startSSE(metroId, sse_id);
    }
  };

  let eventSource: any;
  let retryTimeout = 1000;
  let maxTimeout = 30000;
  let reconnecting = false;

  const { storageToken } = useAccount();
  const session = storageToken();

  const startSSE = (metro_id: string, sse_id: string) => {
    if (eventSource) eventSource.close();
    eventSource = new EventSource(`${import.meta.env.VITE_API}/check_metro_test_status?metro_id=${metro_id}&sse_id=${sse_id}`);
    eventSource.onmessage = (event: any) => {
      toast.success(event.data, {autoClose: false});
      retryTimeout = 1000;
      stopSSE();
    },
    eventSource.onerror = (event: any) => {
      console.error('Connection lost.');
      if (eventSource.readyState === EventSource.CLOSED) {
        console.log('Server closed connection normally. Not reconnecting.');
        return;
      }
      console.log('Retrying in', retryTimeout / 1000, 'seconds...');
      eventSource.close();
      eventSource = null;
      if (!reconnecting) {
        reconnecting = true;
        setTimeout(() => {
          reconnecting = false;
          retryTimeout = Math.min(retryTimeout * 2, maxTimeout);
          startSSE(metro_id, sse_id);
        }, retryTimeout);
      }
    };
  }

  const stopSSE = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
      console.log('SSE connection closed by user.');
    }
  }

  const handleRetrieveResult = () => {
    setShowRetrieveResultDialog(true);
  }

  return (
    <PageContent>
      <PageHeader pageName="Metro Ethernet NIDs" count={data?.data?.length || 0} />
      <Box sx={{ mb: 5, position: 'absolute', top: 140, left: 570 }}>
        <Select options={entities} defaultValue={entities[0]} value={entity} onChange={changeEntity} styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: '8px',
            width: '300px',
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            width: '300px',
          }),
        }}/>
      </Box>
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.metro_id,
                      };
                    }) || []
                  }
                  newLabel="Create Device"
                  columns={columns}
                  columnVisibilityModel={columnVisible}
                  rowSelectionModel={rowSelectionModel}
                  handleSelectionChange={handleSelectionChange}
                  itemsSelected={rowSelectionModel.length === 1}
                  newOnClick={handleCreateModalOpen}
                  refreshData={handleDataRefetch}
                  pagination={true}
                  menuItems={[
                    {
                      label: 'Edit NID info',
                      icon: <EditIcon color="info" fontSize="small" />,
                      onClick: handleEditNIDInfo,
                    },
                    {
                      label: 'Edit OAM info',
                      icon: null,
                      onClick: handleEditOamCcmInfo,
                    },
                    {
                      label: 'Assign Entity',
                      icon: null,
                      onClick: () => setShowAssignEntity(true),
                    },
                    {
                      label: 'Start Activation Test',
                      icon: <SpeedIcon color="info" fontSize="small" />,
                      onClick: handleActivationTest,
                      hasDivider: true,
                    },
                    {
                      label: 'Retrieve Activation Test Report',
                      icon: null,
                      onClick: handleRetrieveResult,
                    },
                    {
                      label: 'Edit Appflow',
                      icon: <ToggleOnIcon color="info" fontSize="small" />,
                      onClick: handleEditNetflow,
                      hasDivider: true,
                    },
                    // {
                    //   label: 'Edit Geo Visibility',
                    //   icon: <TravelExploreIcon color="info" fontSize="small" />,
                    //   onClick: handleEditGeo,
                    //   hasDivider: true,
                    // }, {
                    //   label: 'Edit Block Malicious Sources',
                    //   icon: null,
                    //   onClick: handleBlockMalicious,
                    // }, {
                    //   label: 'Edit DDoS Mitigation',
                    //   icon: null,
                    //   onClick: handleDDosMitigation,
                    // },
                    {
                      label: 'Get Diagnostics',
                      icon: <TroubleshootIcon color="info" fontSize="small" />,
                      onClick: handleGetDiagnostic,
                      hasDivider: true,
                    },
                    {
                      label: 'Reset LAN Ports',
                      icon: null,
                      onClick: handleResetLAN,
                    },
                    {
                      label: 'Reset Device',
                      icon: null,
                      onClick: handleResetDevice,
                    },
                    {
                      label: 'Ping and Traceroute',
                      icon: null,
                      onClick: handlePing,
                    },
                    {
                      label: 'Delete',
                      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                      onClick: () => setShowConfirmDelete(true),
                      hasDivider: true,
                    }
                  ]}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={[
                      {
                        el: <EthernetDeviceTabDetails data={detailData?.data} />,
                        label: 'Details',
                        id: 0
                      },
                      {
                        el: <NodeMetrics id={selectedRowItem} />,
                        label: 'Monitoring',
                        id: 1,
                      }
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {showCreateDeviceDialog && (
        <CreateModal
          open={showCreateDeviceDialog}
          handleClose={handleCreateModalClose}
          title="Create Metro Ethernet NID Device"
        >
          <EthernetDeviceCreate handleClose={handleCreateModalClose} />
        </CreateModal>
      )}
      {showEditNIDInfoDialog && selectedItemId && detailData && selectedItemWanInfo && (
        <DialogStandard
          open={showEditNIDInfoDialog}
          handleClose={() => setShowEditNIDInfoDialog(false)}
          title="Edit NID info"
        >
          <EthernetDeviceNIDInfoEdit
            onSubmit={() => {
              setShowEditNIDInfoDialog(false);
              refetch();
              detailRefetch();
            }}
            metroId={selectedItemId}
            metroName={detailData?.data.metro_name}
            shippingAddress={detailData?.data?.shipping_address || ''}
            siteAddress={detailData?.data?.site_address || ''}
            providerServiceID={detailData?.data?.provider_service_id || ''}
            clientCircuitID={detailData?.data?.client_circuit_id || ''}
            localContactName={detailData?.data?.contact_name || ''}
            localContactEmail={detailData?.data?.contact_email || ''}
            description={detailData?.data?.description || ''}
            wanCirMbps={detailData?.data?.wan_cir_mbps?.toString() || ''}
            wanEirMbps={detailData?.data?.wan_eir_mbps?.toString() || ''}
            wanUserDataVlanId={selectedItemWanInfo.vlan_ids.vlan_user_data || ''}
            wanQInQVlanId={selectedItemWanInfo.vlan_ids.vlan_q_in_q || ''}
            wanMgmtVlanId={selectedItemWanInfo.vlan_ids.vlan_mgmt || ''}
          />
        </DialogStandard>
      )}
      {showEditOamCcmInfoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditOamCcmInfoDialog}
          handleClose={() => setShowEditOamCcmInfoDialog(false)}
          title="Edit OAM info"
        >
          <EthernetDeviceOamCcmInfoEdit
            onSubmit={() => {
              setShowEditOamCcmInfoDialog(false);
              refetch();
              detailRefetch();
            }}
            metroID={selectedItemId}
            loopbackEnabled={detailData?.data.oam_loopback_enabled || false}
            ccpEnabled={detailData?.data.oam_ccp_enabled || false}
            ccmInterval={detailData?.data.oam_ccm_interval?.toString() || ''}
            mdName={detailData?.data.oam_md_name || ''}
            mdLevel={detailData?.data.oam_md_level?.toString() || ''}
            mdAssociation={detailData?.data.oam_md_association || ''}
            mepID={detailData?.data.oam_mep_id?.toString() || ''}
            oamVlanID={detailData?.data.oam_vlan_id?.toString() || ''}
          />
        </DialogStandard>
      )}
      {showEditNetflowDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditNetflowDialog}
          handleClose={() => setShowEditNetflowDialog(false)}
          title="Edit Appflow"
        >
          <EthernetDeviceNetflowEdit
            onSubmit={() => {
              setShowEditNetflowDialog(false);
              refetch();
              detailRefetch();
            }}
            metroId={selectedItemId}
            netflowEnabled={detailData?.data.netflow_enabled || false}
          />
        </DialogStandard>
      )}
      {showEditGeoDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showEditGeoDialog}
          handleClose={() => setShowEditGeoDialog(false)}
          title="Edit Geo Visibility"
        >
          <EthernetDeviceGeoVisibilityEdit
            onSubmit={() => {
              setShowEditGeoDialog(false);
              refetch();
              detailRefetch();
            }}
            metroId={selectedItemId}
            geoEnabled={(detailData?.data.monitor_inbound_pub_ip_enabled && detailData?.data.monitor_outbound_pub_ip_enabled) || false}
          />
        </DialogStandard>
      )}
      {showEditBlockMalicious && selectedItemId && detailData && (
        <DialogStandard
          open={showEditBlockMalicious}
          handleClose={() => setShowEditBlockMalicious(false)}
          title="Edit Block Malicious Sources"
        >
          <EthernetDeviceBlockMaliciousEdit
            onSubmit={() => {
              setShowEditBlockMalicious(false);
              refetch();
              detailRefetch();
            }}
            metroId={selectedItemId}
            isEnabled={detailData.data.malicious_ip_block_enabled || false}
          />
        </DialogStandard>
      )}
      {showEditDDosMitigation && selectedItemId && detailData && (
        <DialogStandard
          open={showEditDDosMitigation}
          handleClose={() => setShowEditDDosMitigation(false)}
          title="Edit DDoS Mitigation"
        >
          <EthernetDeviceDDosMitigationEdit
            onSubmit={() => {
              setShowEditDDosMitigation(false);
              refetch();
              detailRefetch();
            }}
            metroId={selectedItemId}
            dDosEnabled={detailData.data.denial_of_service_enabled || false}
          />
        </DialogStandard>
      )}
      {showGetDiagnosticDialog && selectedItemId && (
        <DialogStandard
          size='md'
          open={showGetDiagnosticDialog}
          handleClose={() => setShowGetDiagnosticDialog(false)}
          title="Get Diagnostics"
        >
          <RouterGetDiagnostic
            cpeId={selectedItemId}
            onSubmit={() => setShowGetDiagnosticDialog(false)}
          />
        </DialogStandard>
      )}
      {showResetLANPortsDialog && selectedItemId && (
        <DialogStandard
          open={showResetLANPortsDialog}
          handleClose={() => setShowResetLANPortsDialog(false)}
          title="Reset LAN Ports"
        >
          <EthernetDeviceResetLANPort
            metroId={selectedItemId}
            onSubmit={() =>{
              setShowResetLANPortsDialog(false);
              refetch();
              detailRefetch();
            }}
          />
        </DialogStandard>
      )}
      {showResetDeviceDialog && (
        <DialogConfirm
          open={showResetDeviceDialog}
          handleClose={() => setShowResetDeviceDialog(false)}
          handleConfirm={handleRouterResetDevice}
          title="Reset Metro?"
          description={`Are you certain you wish to proceed with resetting ${selectedItemId}? Please note that this action is irreversible.`}
        />
      )}
      {showPingDialog && selectedItemId && (
        <DialogStandard
          size='md'
          open={showPingDialog}
          handleClose={() => setShowPingDialog(false)}
          title="Ping & Traceroute"
        >
          <RouterPingAndTraceroute
            cpeId={selectedItemId}
          />
        </DialogStandard>
      )}
      {/* {showLoopbackTestDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showLoopbackTestDialog}
          handleClose={() => setShowLoopbackTestDialog(false)}
          title="Start Loopback Test"
        >
          <EthernetDeviceLoopbackTest
            metroID={selectedItemId}
            close={() => { setShowLoopbackTestDialog(false) }}
            oamVlanID={detailData?.data.oam_vlan_id?.toString() || ''}
            destMacAddr={detailData?.data.ccm_dest_mac_addr || ''}
            megID={detailData?.data.ccm_meg_id?.toString() || ''}
          />
        </DialogStandard>
      )} */}
      {showActivationTestDialog && selectedItemId && (
        <DialogStandard
          open={showActivationTestDialog}
          handleClose={() => setShowActivationTestDialog(false)}
          title='Start Activation Test'
        >
          <EthernetDeviceActivationTest
            metroId={selectedItemId}
            close={handleActivationTestDialogClose}
          />
        </DialogStandard>
      )}
      {showRetrieveResultDialog && selectedItemId && (
        <DialogStandard
          size='md'
          open={showRetrieveResultDialog}
          handleClose={() => setShowRetrieveResultDialog(false)}
          title="Retrieve Activation Test Report"
        >
          <EthernetDeviceDownloadResult metroId={selectedItemId}/>
        </DialogStandard>
      )}
      {showAssignEntity && selectedItemId && detailData && (
        <DialogStandard
          open={showAssignEntity}
          handleClose={() => setShowAssignEntity(false)}
          title="Assign Entity"
        >
          <EthernetAssignEntity
            onSubmit={() => {
              setShowAssignEntity(false);
              refetch();
              detailRefetch();
            }}
            metroId={selectedItemId}
            entityName={detailData?.data?.entity_name}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleMetroDelete}
          title="Delete Metro Device?"
          description={`Are you certain you wish to proceed with deleting
            ${selectedItemId}? Please note that this action is irreversible and
            will result in the permanent removal of Metro Device.`}
          confirmationPhrase={selectedItemId}
        />
      )}
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </PageContent>
  );
}
